// SovEU hero diagram — institutional governance / data registry visual.
// Combines abstract registers (rectangles) at the top, a central spine,
// and a layer of nodes representing connected systems / organisations.
// Inherits color via currentColor when needed; uses muted blue tones on navy.

export default function NetworkDiagram({ className = "" }) {
  // Top-row "registers" (governance documents / registry entries)
  const registers = [
    { x: 60, y: 50, w: 110, h: 56 },
    { x: 195, y: 35, w: 110, h: 56 },
    { x: 330, y: 60, w: 110, h: 56 },
  ];

  // Bottom-row "system nodes" (organisations, data sources, services)
  const nodes = [
    { cx: 80, cy: 320, r: 7 },
    { cx: 160, cy: 360, r: 6 },
    { cx: 245, cy: 340, r: 9, primary: true },
    { cx: 330, cy: 370, r: 6 },
    { cx: 410, cy: 330, r: 7 },
    { cx: 120, cy: 400, r: 5 },
    { cx: 360, cy: 405, r: 5 },
  ];

  // Connections between registers and nodes (governance flow)
  const flows = [
    { from: { x: 115, y: 106 }, to: { cx: 80, cy: 320 } },
    { from: { x: 115, y: 106 }, to: { cx: 160, cy: 360 } },
    { from: { x: 250, y: 91 }, to: { cx: 245, cy: 340 } },
    { from: { x: 250, y: 91 }, to: { cx: 330, cy: 370 } },
    { from: { x: 385, y: 116 }, to: { cx: 330, cy: 370 } },
    { from: { x: 385, y: 116 }, to: { cx: 410, cy: 330 } },
  ];

  // Inter-node lateral connections
  const lateral = [
    [0, 1], [1, 2], [2, 3], [3, 4], [1, 5], [3, 6], [2, 5], [2, 6],
  ];

  return (
    <svg
      viewBox="0 0 480 460"
      className={className}
      role="img"
      aria-label="Abstract governance- en datadiagram"
      data-testid="hero-network-diagram"
    >
      <defs>
        <radialGradient id="nd-glow" cx="50%" cy="55%" r="55%">
          <stop offset="0%" stopColor="#2D6F8F" stopOpacity="0.30" />
          <stop offset="100%" stopColor="#2D6F8F" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Soft atmospheric glow */}
      <circle cx="240" cy="240" r="230" fill="url(#nd-glow)" />

      {/* Subtle horizontal guide rules */}
      <line x1="20" y1="220" x2="460" y2="220" stroke="#4A6B8F" strokeOpacity="0.18" strokeDasharray="2 6" />

      {/* Register rectangles (top row) */}
      {registers.map((r, i) => (
        <g key={`reg-${i}`}>
          <rect
            x={r.x}
            y={r.y}
            width={r.w}
            height={r.h}
            rx="6"
            ry="6"
            fill="#1E3A5F"
            stroke="#6B8CAE"
            strokeOpacity="0.7"
            strokeWidth="1"
          />
          {/* "header bar" inside register */}
          <rect
            x={r.x + 12}
            y={r.y + 12}
            width={r.w - 50}
            height="4"
            rx="2"
            fill="#9DB6D1"
            opacity="0.85"
          />
          {/* "lines" inside register */}
          <rect x={r.x + 12} y={r.y + 24} width={r.w - 24} height="2.5" rx="1" fill="#6B8CAE" opacity="0.6" />
          <rect x={r.x + 12} y={r.y + 32} width={r.w - 38} height="2.5" rx="1" fill="#6B8CAE" opacity="0.45" />
          <rect x={r.x + 12} y={r.y + 40} width={r.w - 30} height="2.5" rx="1" fill="#6B8CAE" opacity="0.45" />
        </g>
      ))}

      {/* Flow lines from registers down to nodes */}
      {flows.map((f, i) => (
        <line
          key={`flow-${i}`}
          x1={f.from.x}
          y1={f.from.y}
          x2={f.to.cx}
          y2={f.to.cy}
          stroke="#6B8CAE"
          strokeOpacity="0.4"
          strokeWidth="1"
        />
      ))}

      {/* Lateral connections between nodes */}
      {lateral.map(([a, b], i) => {
        const na = nodes[a];
        const nb = nodes[b];
        return (
          <line
            key={`lat-${i}`}
            x1={na.cx}
            y1={na.cy}
            x2={nb.cx}
            y2={nb.cy}
            stroke="#6B8CAE"
            strokeOpacity="0.3"
            strokeWidth="1"
          />
        );
      })}

      {/* Node circles */}
      {nodes.map((n, i) => (
        <g key={`node-${i}`}>
          {n.primary && (
            <circle
              cx={n.cx}
              cy={n.cy}
              r={n.r + 7}
              fill="#2D6F8F"
              fillOpacity="0.18"
            />
          )}
          <circle
            cx={n.cx}
            cy={n.cy}
            r={n.r}
            fill={n.primary ? "#FFFFFF" : "#CBD6E5"}
            stroke={n.primary ? "#2D6F8F" : "#6B8CAE"}
            strokeWidth={n.primary ? 2 : 1}
          />
        </g>
      ))}

      {/* Caption-style markers (small abstract bars at the very bottom) */}
      <g opacity="0.5">
        <rect x="60" y="430" width="20" height="3" rx="1.5" fill="#6B8CAE" />
        <rect x="90" y="430" width="36" height="3" rx="1.5" fill="#6B8CAE" />
        <rect x="135" y="430" width="14" height="3" rx="1.5" fill="#6B8CAE" />
        <rect x="380" y="430" width="22" height="3" rx="1.5" fill="#6B8CAE" />
        <rect x="410" y="430" width="32" height="3" rx="1.5" fill="#6B8CAE" />
      </g>
    </svg>
  );
}
